import { sessionQueryVersionAtom } from '@/atoms/sessionAtoms';
import {
  IS_CONSOLIDATED_SESSIONS_LIST_ENABLED,
  IS_SIDEBAR_PANEL_ENABLED,
} from '@/components/constants/flags';
import { getSidebarPanelOverlayTriggerProps } from '@/components/layout/Navigation/sidebar-panel/sidebar-overlay-constants';
import { OfflineGatedTooltip } from '@/components/offline-recording/offline-gated-tooltip';
import { useIsSessionUrgencyEnabled } from '@/features/flags';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { TypographyCaptionBold } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { SessionScope } from '@heidi/schema/ml-scribe';
import { useAtom } from 'jotai';
import { ListFilter, RotateCcw } from 'lucide-react';
import { useEffect } from 'react';
import { isClinicianFilterApplicableForScope, sessionListScopeAtom } from './atoms';
import { ClinicianFilter } from './clinician-filter';
import { DateFilter } from './date-filter';
import { useSessionsFilters } from './hooks/useSessionsFilters';
import { PatientProfileFilter } from './patient-profile-filter';
import { StatusFilter } from './status-filter';
import { UrgencyFilter } from './urgency-filter';

export const SessionsFilters = () => {
  const { t } = useTranslations();
  const { shouldGateControls: isOfflineGated } = useOfflineGating();
  const isConsolidatedSessionsEnabled = IS_CONSOLIDATED_SESSIONS_LIST_ENABLED();
  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
  const { isOpen, setIsOpen, isFilterApplied, handleClearFilters } = useSessionsFilters();
  // Going offline gates the filter dropdown, so close it (its `isOpen` lives in a
  // module atom that would otherwise persist a stale `true` across the gate) and
  // clear any applied filters: the dropdown is the only way to change them, so
  // leaving a narrowed cached list with no reset would strand the user offline.
  useEffect(() => {
    if (!isOfflineGated) return;
    if (isOpen) setIsOpen(false);
    if (isFilterApplied) handleClearFilters();
  }, [isOfflineGated, isOpen, isFilterApplied, setIsOpen, handleClearFilters]);
  const { isTogetherOrHigherTier } = useQueryUserTier();
  const isSessionUrgencyEnabled = useIsSessionUrgencyEnabled();
  const [scope, setScope] = useAtom(sessionListScopeAtom);
  const showClinicianFilter = isClinicianFilterApplicableForScope(scope);
  // This legacy toolbar has no scope UI, so pin the shared scope to `me`: a
  // persisted `all`/`direct_share` (left by the new list after a flag rollback)
  // would otherwise show a clinician filter the legacy v3 query never sends.
  useEffect(() => {
    if (scope !== SessionScope.me) {
      setScope(SessionScope.me);
    }
  }, [scope, setScope]);
  const [sessionQueryVersion, setSessionQueryVersion] = useAtom(sessionQueryVersionAtom);
  useEffect(() => {
    if (isOpen && sessionQueryVersion === 'v2') {
      setSessionQueryVersion('v1');
    }
  }, [isOpen, sessionQueryVersion, setSessionQueryVersion]);

  const filterTrigger = (
    <Button
      data-testid="session-list-filter-button"
      size="xxs"
      iconOnly
      variant={isSidebarPanelEnabled ? 'panel_ghost' : 'ghost'}
      disabled={isOfflineGated}
      data-active={isSidebarPanelEnabled ? !!(isFilterApplied || isOpen) : undefined}
      className={
        !isSidebarPanelEnabled
          ? cn(
              'text-text-secondary',
              isFilterApplied &&
                'bg-surface-selected text-text-active hover:bg-surface-selected-hover hover:text-text-active',
              isOpen && 'bg-background-secondary text-text-primary'
            )
          : undefined
      }
      aria-label="Filter sessions"
    >
      <ListFilter aria-hidden="true" />
    </Button>
  );

  // Offline: filtering runs client-side over the cached list in the reachable
  // legacy modes, but the trigger is greyed per the offline read-only policy
  // (see useOfflineGating), not out of technical need.
  if (isOfflineGated) {
    return <OfflineGatedTooltip isOfflineGated>{filterTrigger}</OfflineGatedTooltip>;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger
        {...getSidebarPanelOverlayTriggerProps(isSidebarPanelEnabled)}
        asChild
      >
        {filterTrigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          'w-screen max-w-[182px] p-1',
          isConsolidatedSessionsEnabled && 'max-w-[296px]'
        )}
        side="bottom"
        align="start"
      >
        {isTogetherOrHigherTier && <StatusFilter />}
        <DateFilter />

        <PatientProfileFilter />
        {showClinicianFilter && isTogetherOrHigherTier && <ClinicianFilter />}
        {isSessionUrgencyEnabled && <UrgencyFilter />}

        {isFilterApplied && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="text-text-secondary"
                onClick={handleClearFilters}
                onSelect={(e) => e.preventDefault()}
              >
                <RotateCcw className="size-3.5" />
                <TypographyCaptionBold>
                  {t('sessionToolbar.filters.clear')}
                </TypographyCaptionBold>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
